import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface UserInfo {
  user_id: string;
  full_name: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly API_URL = '/api/v1';
  private readonly USER_DATA_KEY = 'user_data';
  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  public user$ = this.userSubject.asObservable();

  constructor(private http: HttpClient) {
    // Load user data from sessionStorage on service initialization
    this.loadUserFromStorage();
  }

  /**
   * Checks if the user is authenticated and retrieves their information
   */
  ping(): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${this.API_URL}/ping`).pipe(
      tap(userInfo => {
        this.setUser(userInfo);
      })
    );
  }

  pingV2(): Observable<UserInfo> {
    const v2Url = this.API_URL.replace('/v1', '/v2');
    return this.http.get<UserInfo>(`${v2Url}/ping`).pipe(
      tap(userInfo => {
        this.setUser(userInfo);
      })
    );
  }

  /**
   * Gets the current user information
   */
  getCurrentUser(): UserInfo | null {
    return this.userSubject.value;
  }

  /**
   * Sets user data and persists it to sessionStorage
   */
  private setUser(userInfo: UserInfo): void {
    this.userSubject.next(userInfo);
    sessionStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userInfo));
  }

  /**
   * Loads user data from sessionStorage if available
   */
  private loadUserFromStorage(): void {
    try {
      const userData = sessionStorage.getItem(this.USER_DATA_KEY);
      if (userData) {
        const userInfo: UserInfo = JSON.parse(userData);
        this.userSubject.next(userInfo);
      }
    } catch (error) {
      console.error('Error loading user data from storage:', error);
      // Clear corrupted data
      sessionStorage.removeItem(this.USER_DATA_KEY);
    }
  }

  /**
   * Clears user data from memory and storage
   */
  clearUser(): void {
    this.userSubject.next(null);
    sessionStorage.removeItem(this.USER_DATA_KEY);
  }
} 